import '@/global.css';
import '@/i18n/config'; // Import i18n configuration
import '@/i18n/dayjs-config'; // Import dayjs configuration
import '@/services/media/init';
import * as Sentry from '@sentry/react-native';
import { browserSessionIntegration } from '@sentry/browser';
import * as SplashScreen from 'expo-splash-screen';
import * as ScreenOrientation from 'expo-screen-orientation';
import { Stack } from 'expo-router';
import { Platform } from 'react-native';
import * as Device from 'expo-device';
import { AuthProvider } from '~/components/providers/AuthProvider';
import { MediaConfigSync } from '@/components/providers/MediaConfigSync';
import { AppLinkProvider } from '@/components/providers/AppLinkProvider';
import { SignalRProvider } from '~/components/providers/SignalRProvider';
import { NotificationSoundPlayer } from '~/components/providers/NotificationSoundPlayer';
import { ThemeProvider } from '~/components/providers/ThemeProvider';
import { MeasurementsProvider } from '~/components/providers/MeasurementsProvider';
import { PushProvider } from '~/components/providers/PushProvider';
import { IAPProvider } from '~/components/providers/IAPProvider';
import { LocationProvider } from '@/components/providers/LocationProvider';
import { PermissionsBootstrap } from '@/components/providers/PermissionsBootstrap';
import { CommunitySwitchOverlay } from '~/components/ui/CommunitySwitchOverlay';
import { ImageViewerProvider } from '~/components/ui/image-viewer/ImageViewerProvider';
import { ImageViewerWrapper } from '~/components/ui/image-viewer/ImageViewerWrapper';
import { AppErrorBoundary } from '~/components/providers/AppErrorBoundary';
import { VersionCheckProvider } from '~/components/providers/VersionCheckProvider';
import { WhatsNewProvider } from '~/components/providers/WhatsNewProvider';
import { AndroidBridgeProvider } from '@/components/providers/AndroidBridgeProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect } from 'react';
import { SENTRY_RELEASE, APP_VERSION, BUILD_NUMBER } from '@/constants/version';
import {
  useSentryUserContext,
  useSentryRouteContext,
  sentryBeforeSend,
} from '@/services/SentryService';
import { startReactRootDiagnostics } from '@/services/ReactRootDiagnostics';
import { SafeAreaProvider, initialWindowMetrics } from 'react-native-safe-area-context';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { cssInterop } from 'nativewind';
import { FlashList } from '@shopify/flash-list';
import { Image as ExpoImage } from 'expo-image';
import { useAppState } from '@/hooks/useAppState';

cssInterop(FlashList, {
  className: 'style',
  contentContainerClassName: 'contentContainerStyle',
});

// Cap iOS SDWebImage disk cache at 500 MB. Android uses its own cache policy.
if (Platform.OS === 'ios') {
  ExpoImage.configureCache({ maxDiskSize: 500 * 1024 * 1024 });
}

// Prevent the splash screen from auto-hiding before App component declaration
SplashScreen.preventAutoHideAsync();
SplashScreen.setOptions({
  fade: true,
});

// Initialize Sentry
Sentry.init({
  dsn: 'https://df1d49e181aa22a48c9e8b2b127cd8fe@o4509588075511808.ingest.us.sentry.io/4509588272119809',
  release: SENTRY_RELEASE,
  dist: BUILD_NUMBER ?? undefined,
  environment: __DEV__ ? 'development' : 'production',
  sendDefaultPii: true,
  // Release Health / adoption. Native (iOS/Android) tracks sessions by default,
  // but on web @sentry/react-native only adds browserSessionIntegration when this
  // is explicitly truthy. Without it, web emits no sessions and the Releases page
  // shows no web adoption or crash-free data.
  enableAutoSessionTracking: true,
  // Web only: the default web session integration starts a new session on every
  // route change (lifecycle: 'route'), which is noisy for our SPA. Override it
  // with a per-page-load session (lifecycle: 'page') to keep session volume low.
  // The page-mode (non-default) integration wins over the auto-added route-mode
  // one during integration de-duplication. Native session tracking is handled by
  // the native SDK and is left untouched.
  integrations: (defaultIntegrations) =>
    Platform.OS === 'web'
      ? [...defaultIntegrations, browserSessionIntegration({ lifecycle: 'page' })]
      : defaultIntegrations,
  // Android 12+: capture native crashes via ApplicationExitInfo (tombstone collection).
  enableTombstone: true,
  // iOS App Hang detection: raise threshold from default 2s to 5s.
  // Filters out short cosmetic hangs while still capturing catastrophic
  // main-thread freezes. iOS-only option; ignored on Android/web.
  appHangTimeoutInterval: 5,
  // Drops noise events that are not actionable app bugs (expected ApiErrors,
  // disk-full AsyncStorage writes, UIKit keyboard App Hangs) — see SentryService.
  beforeSend: sentryBeforeSend,
  ignoreErrors: [
    // This error is caused by third-party Safari browser extensions on iOS attempting
    // to communicate with tabs that no longer exist, not by application code.
    'Invalid call to runtime.sendMessage(). Tab not found.',
    // Ignore generic network errors since they are usually caused by the user's connection
    'Network Error',
    // Safari surfaces failed image/resource loads as unhandled promise rejections
    'Load failed',
  ],
  // Web only: keep errors thrown from our Expo bundle and drop the ones thrown by
  // browser extensions and injected scripts (chrome-extension://, Safari's masked
  // extension URLs, Google Translate). Sentry decides from the innermost stack frame,
  // so an app error that surfaces inside an extension's wrapper around a browser API
  // (XHR, fetch, addEventListener) is dropped too. This filter runs before
  // RewriteFrames, which shortens every filename to app:///<name> and hides extension
  // URLs from Sentry's server-side filter. The second pattern matches that rewritten
  // form, so our own errors still get through if the integration order ever flips.
  allowUrls:
    Platform.OS === 'web' && !__DEV__
      ? [/^https?:\/\/[^/]+\/_expo\/static\/js\/web\//, /^app:\/\/\/[\w-]+-[0-9a-f]{32}\.js$/]
      : undefined,
  initialScope: {
    tags: {
      platform: Platform.OS,
      appVersion: APP_VERSION,
      buildNumber: BUILD_NUMBER ?? 'unknown',
      deviceBrand: Device.brand ?? 'unknown',
      osVersion: Device.osVersion ?? 'unknown',
      isEmulator: Device.isDevice ? 'false' : 'true',
    },
  },
});

startReactRootDiagnostics();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Web flips React Query offline on browser events, which would pause
      // queries/mutations (silent hang, no error, endless skeletons) until
      // connectivity returns. Dispatch anyway so failures surface like native.
      networkMode: 'always',
      retry: (failureCount, error: any) => {
        // Don't retry on client errors (4xx) as they're permanent
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }

        // Retry up to 1 time for network errors and server errors (5xx)
        return failureCount < 1;
      },
      staleTime: 5000, // 5 seconds
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      networkMode: 'always',
    },
  },
});

export const unstable_settings = {
  initialRouteName: '',
};

// Component that observes auth state and route changes to update Sentry context
function SentryContextObserver({ children }: { children: React.ReactNode }) {
  useSentryUserContext();
  useSentryRouteContext();
  return <>{children}</>;
}

function RootLayout() {
  // Required by the legacy SignalR library for window event listeners
  // And to fix issues with React Query's focusManager during hot reload in dev mode
  require('@/libs/polyfills/window-polyfill');

  useLanguage(); // Sets language based on configuration at startup

  // Drop decoded image bitmaps from memory when the app is backgrounded so
  // iOS reclaims a large chunk of RAM before the next foreground measurement.
  // The disk cache survives, so re-display is fast.
  useAppState({
    onBackground: () => {
      ExpoImage.clearMemoryCache();
      console.log('[AppState:ExpoImage] Cleared image memory cache');
    },
  });

  useEffect(() => {
    const init = async () => {
      // On Android tablets, unlock orientation to prevent letterboxing on landscape devices.
      // This runs before splash hide so the window resize happens behind the splash screen.
      if (Platform.OS === 'android' && Device.deviceType === Device.DeviceType.TABLET) {
        await ScreenOrientation.unlockAsync();
      }
      SplashScreen.hide();
      if (Platform.OS === 'web') {
        document.getElementById('loading-splash')?.remove();
      }
    };
    init();
  }, []);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
    <AppErrorBoundary>
      <VersionCheckProvider>
        <SafeAreaProvider initialMetrics={initialWindowMetrics}>
          <AndroidBridgeProvider>
          <KeyboardProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <MediaConfigSync />
              <SentryContextObserver>
                <AppLinkProvider>
                  <PermissionsBootstrap>
                    <LocationProvider>
                      <SignalRProvider loggingLevel="none">
                        <NotificationSoundPlayer />
                        <MeasurementsProvider>
                          <ThemeProvider>
                            <PushProvider>
                              <IAPProvider>
                              <BottomSheetModalProvider>
                              <ImageViewerProvider>
                                <Stack screenOptions={{ headerShown: false, animation: 'none' }}>
                                  {/* Order of Stack.Screen is important for Android - the first one is what the app will load with */}
                                  <Stack.Screen name="index" />
                                  <Stack.Screen name="(app)" />
                                  <Stack.Screen name="(auth-web)" />
                                </Stack>
                                <ImageViewerWrapper />
                                <WhatsNewProvider />
                                <CommunitySwitchOverlay />
                              </ImageViewerProvider>
                              </BottomSheetModalProvider>
                              </IAPProvider>
                            </PushProvider>
                          </ThemeProvider>
                        </MeasurementsProvider>
                      </SignalRProvider>
                    </LocationProvider>
                  </PermissionsBootstrap>
                </AppLinkProvider>
              </SentryContextObserver>
            </AuthProvider>
          </QueryClientProvider>
          </KeyboardProvider>
          </AndroidBridgeProvider>
        </SafeAreaProvider>
      </VersionCheckProvider>
    </AppErrorBoundary>
    </GestureHandlerRootView>
  );
}

// Wrap the app with Sentry for automatic instrumentation
export default Sentry.wrap(RootLayout);
